import { Base64 } from 'js-base64';
import { useEffect, useRef } from 'react';

import { MESSAGE_KEYWORD, useHandleApp } from '@/modules/wello-app';

import { useCheckWelloApp } from './useCheckWelloApp';

interface UseFileUploadProps {
  accept?: string;
  multiple?: boolean;
}

export const useFileUpload = ({
  accept,
  multiple = false,
}: UseFileUploadProps) => {
  const { request, response } = useHandleApp<{
    image: string;
  }>({
    type: MESSAGE_KEYWORD.IMAGE,
  });

  const { isWelloApp } = useCheckWelloApp();

  const image64 = response?.data?.image;

  const resolveRef = useRef<((value: File | undefined) => void) | null>(null);
  const resolveFilesRef = useRef<((value: File[]) => void) | null>(null);

  const file = useRef<File | undefined>(undefined);
  const files = useRef<File[]>([]);

  useEffect(() => {
    if (!image64) return;

    const mime = 'image/png';

    // Base64 문자열을 바이트 코드 배열로 디코딩
    const byteCharacters = Base64.atob(image64);
    const byteArrays: BlobPart[] = [];

    // 512바이트씩 처리
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
      const slice = byteCharacters.slice(offset, offset + 512);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.codePointAt(i) ?? 0;
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    //* Uint8Array의 배열을 Blob으로 변환
    const blob = new Blob(byteArrays, { type: mime });

    // Blob을 File로 변환
    file.current = new File([blob], 'image.png', { type: mime });
    resolveRef.current?.(file.current);
    resolveRef.current = null;
    if (files.current.length === 0 && file.current) {
      files.current = [file.current];
    }
    resolveFilesRef.current?.(files.current);
    resolveFilesRef.current = null;
  }, [image64]);

  const openFilePicker = (pickMultiple: boolean) => {
    //! 혹시 남아있을지 모를 대기중인 응답을 초기화
    resolveRef.current?.(undefined);
    resolveRef.current = null;
    resolveFilesRef.current?.([]);
    resolveFilesRef.current = null;
    file.current = undefined;
    files.current = [];

    if (isWelloApp) {
      request();
    } else {
      const fileInput = document.createElement('input');

      fileInput.type = 'file';
      if (accept) fileInput.accept = accept;
      if (pickMultiple) fileInput.multiple = true;

      fileInput.style.appearance = 'hide';
      fileInput.addEventListener('change', (event) => {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
          file.current = input.files[0];
          files.current = Array.from(input.files);
        }
        resolveRef.current?.(file.current);
        resolveRef.current = null;
        resolveFilesRef.current?.(files.current);
        resolveFilesRef.current = null;
      });

      fileInput.addEventListener('cancel', () => {
        resolveRef.current?.(undefined);
        resolveRef.current = null;
        resolveFilesRef.current?.([]);
        resolveFilesRef.current = null;
      });

      fileInput.click();
    }
  };

  return {
    getFile: async () => {
      openFilePicker(false);

      return new Promise<File | undefined>((resolve) => {
        resolveRef.current = resolve;
      });
    },
    getFiles: async () => {
      openFilePicker(multiple);

      return new Promise<File[]>((resolve) => {
        resolveFilesRef.current = resolve;
      });
    },
  };
};
